<template>
  <div class="flex flex-col h-full text-white bg-gray-900">
    <!-- Header -->
    <div class="flex gap-3 items-center py-3 px-4 border-b border-gray-800">
      <button
        v-if="showBackButton"
        class="p-2 -ml-2 text-gray-400 transition-colors hover:text-white"
        @click="$emit('back')">
        <fa-icon icon="arrow-left" />
      </button>

      <!-- Participant info -->
      <div v-if="otherParticipant" class="flex-1 min-w-0">
        <UserBlock
          :user="otherParticipant"
          size="md"
          :link-to-profile="true"
          :show-username="true" />
      </div>

      <button
        class="p-2 -mr-2 text-gray-400 transition-colors hover:text-white"
        @click="$emit('close')">
        <fa-icon icon="xmark" />
      </button>
    </div>

    <!-- Content being discussed -->
    <div
      v-if="conversation"
      class="px-4 py-3 border-b border-gray-800 bg-gray-800/50">
      <!-- Review -->
      <div v-if="conversation.review" class="flex gap-3 items-center">
        <div
          class="w-16 h-12 bg-center bg-cover rounded shrink-0"
          :style="getReviewImageStyle(conversation.review)"></div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium truncate">
            {{ conversation.review.title_title }}
          </p>
          <p class="text-xs text-gray-400">
            Review · {{ formatScore(conversation.review.score_overall) }}
          </p>
        </div>
        <router-link
          :to="{
            name: 'WebReviewsSingle',
            params: { id: conversation.review.id }
          }"
          class="text-xs text-indigo-400 hover:text-indigo-300 shrink-0">
          View →
        </router-link>
      </div>

      <!-- Title -->
      <div v-else-if="conversation.title" class="flex gap-3 items-center">
        <img
          v-if="conversation.title.tmdb_poster_path"
          :src="`https://image.tmdb.org/t/p/w92${conversation.title.tmdb_poster_path}`"
          class="object-cover w-10 h-14 rounded shrink-0" />
        <div v-else class="w-10 h-14 bg-gray-700 rounded shrink-0"></div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium truncate">
            {{ conversation.title.title }}
          </p>
          <p class="text-xs text-gray-400">Recommendation</p>
        </div>
        <router-link
          :to="{
            name: 'WebTitleDirect',
            params: { id: conversation.title.id }
          }"
          class="text-xs text-indigo-400 hover:text-indigo-300 shrink-0">
          View →
        </router-link>
      </div>

      <!-- List -->
      <div v-else-if="conversation.list" class="flex gap-3 items-center">
        <div
          class="flex justify-center items-center w-12 h-12 bg-gray-700 rounded shrink-0">
          <fa-icon :icon="['far', 'rectangle-list']" class="text-gray-400" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium truncate">
            {{ conversation.list.title }}
          </p>
          <p class="text-xs text-gray-400">List</p>
        </div>
      </div>
    </div>

    <!-- Messages -->
    <div
      ref="messagesContainer"
      class="overflow-y-auto flex-1 py-4 px-4 space-y-4">
      <!-- Loading state -->
      <div v-if="isLoading" class="flex justify-center py-8">
        <fa-icon icon="spinner-third" spin class="text-2xl text-gray-500" />
      </div>

      <!-- Empty state -->
      <div
        v-else-if="messages.length === 0"
        class="py-8 text-sm text-center text-gray-500">
        No messages yet. Start the conversation!
      </div>

      <!-- Message list -->
      <template v-else>
        <MessageBubble
          v-for="message in messages"
          :key="message.id"
          :message="message"
          :conversation="conversation"
          :is-own="message.sender_id === currentUserId" />
      </template>
    </div>

    <!-- Input area -->
    <div class="py-3 px-4 border-t border-gray-800 shrink-0">
      <!-- Emoji quick reactions (if this is about a review) -->
      <div v-if="conversation?.review && !replyMode" class="flex gap-2 mb-3">
        <button
          v-for="emoji in quickReactions"
          :key="emoji"
          :disabled="isSending"
          class="px-3 py-1.5 bg-gray-800 rounded-full text-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
          @click="sendReaction(emoji)">
          {{ emoji }}
        </button>
        <button
          class="px-3 py-1.5 bg-gray-800 rounded-full text-sm text-gray-300 hover:bg-gray-700 transition-colors"
          @click="replyMode = true">
          Reply...
        </button>
      </div>

      <!-- Text input -->
      <div v-if="replyMode || !conversation?.review" class="flex gap-2">
        <input
          v-model="messageText"
          type="text"
          placeholder="Type a message..."
          :disabled="isSending"
          class="flex-1 px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-full text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
          @keydown.enter="sendMessage" />
        <button
          :disabled="!messageText.trim() || isSending"
          class="px-4 py-2.5 bg-indigo-600 rounded-full text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          @click="sendMessage">
          <fa-icon v-if="isSending" icon="spinner-third" spin />
          <fa-icon v-else icon="paper-plane" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useStoreMessages } from '@/stores/storeMessages'
import { useStoreAuth } from '@/stores/storeAuth'
import { useReviewColors } from '@/use/useReviewColors'
import UserBlock from '@/components/UserBlock.vue'
import MessageBubble from '@/components/Messages/MessageBubble.vue'

const props = defineProps({
  conversationId: {
    type: String,
    required: true
  },
  showBackButton: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['back', 'close'])

const storeMessages = useStoreMessages()
const storeAuth = useStoreAuth()
const { scoreHsl } = useReviewColors()

// State
const messageText = ref('')
const replyMode = ref(false)
const isSending = ref(false)
const messagesContainer = ref(null)

// Quick reactions for reviews
const quickReactions = ['🔥', '❤️', '👏', '😢', '🤔']

// Computed
const currentUserId = computed(() => storeAuth.user?.id)

const conversation = computed(() => storeMessages.activeConversation)

const messages = computed(() => storeMessages.activeMessages)

const isLoading = computed(() => storeMessages.isLoading)

const otherParticipant = computed(() => {
  if (!conversation.value?.participants) return null
  return conversation.value.participants.find(
    (p) => p.user_id !== currentUserId.value
  )
})

// Methods
const getReviewImageStyle = (review) => {
  const color = scoreHsl(parseFloat(review.score_overall || 0))
  if (review.image_path) {
    return {
      backgroundImage: `url(https://image.tmdb.org/t/p/w185${review.image_path})`,
      backgroundColor: color
    }
  }
  return { backgroundColor: color }
}

const formatScore = (score) => {
  return score ? parseFloat(score).toFixed(1) : '-'
}

const scrollToBottom = async () => {
  await nextTick()
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

const sendMessage = async () => {
  if (!messageText.value.trim() || isSending.value) return

  isSending.value = true
  try {
    await storeMessages.reply(props.conversationId, messageText.value.trim())
    messageText.value = ''
    replyMode.value = false
    scrollToBottom()
  } catch (error) {
    console.error('Failed to send message:', error)
  } finally {
    isSending.value = false
  }
}

const sendReaction = async (emoji) => {
  if (isSending.value) return

  isSending.value = true
  try {
    await storeMessages.react(props.conversationId, emoji)
    scrollToBottom()
  } catch (error) {
    console.error('Failed to send reaction:', error)
  } finally {
    isSending.value = false
  }
}

// Load conversation on mount
onMounted(async () => {
  await storeMessages.fetchConversation(props.conversationId)
  await storeMessages.markRead(props.conversationId)
  scrollToBottom()
})

// Watch for new messages and scroll
watch(
  messages,
  () => {
    scrollToBottom()
  },
  { deep: true }
)
</script>
